import type {
  VisitorProfile,
  VisitorProfileFieldUpdate,
} from "@/lib/graphql/queries/generated/profile.generated";

type Values = Record<string, string | null>;

/**
 * After a successful save, decide what the form shows. A key the visitor changed WHILE the save was
 * in flight (its current value differs from what was submitted) keeps the visitor's text and keeps
 * the form dirty; every other key takes the saved value. Resetting the whole form to the saved
 * profile threw such typing away.
 */
export function reconcileAfterSave({
  sent,
  current,
  saved,
}: {
  /** The form values at the moment Save was pressed. */
  sent: Values;
  /** The form values now (after the response). */
  current: Values;
  /** The profile as saved (seed values). */
  saved: Values;
}): { values: Values; hasEdits: boolean } {
  const values: Values = { ...saved };
  let hasEdits = false;
  for (const key of new Set([...Object.keys(sent), ...Object.keys(current)])) {
    const now = current[key] ?? null;
    if (now === (sent[key] ?? null)) continue;
    values[key] = now;
    if (now !== (saved[key] ?? null)) hasEdits = true;
  }
  return { values, hasEdits };
}

/** The profile with the submitted field values written onto it (an empty value becomes `null`).
 *  Used when the save is applied locally first (offline queue, demo) so the form's source of
 *  truth already holds it. */
export function applySavedFields(
  profile: VisitorProfile,
  updates: VisitorProfileFieldUpdate[],
): VisitorProfile {
  const byKey = new Map(updates.map((u) => [u.key, u.value]));
  return {
    ...profile,
    fields: profile.fields.map((f) =>
      byKey.has(f.key) ? { ...f, value: byKey.get(f.key) || null } : f,
    ),
  };
}
